import { useState } from "react";
import { DEFAULT_BROWSE_PER_PAGE } from "@shared/api/pagination";

type BrowsePaginationState = {
  page: number;
  perPage: number;
  cursor?: string;
};

/**
 * Stores cursor-based pagination state for browse pages and exposes navigation handlers.
 * @param initialPerPage number - Initial rows-per-page value for the first request.
 * @returns BrowsePaginationState and handlers for next, previous, rows-per-page changes, and reset.
 */
export function useBrowsePagination(initialPerPage = DEFAULT_BROWSE_PER_PAGE) {
  const [state, setState] = useState<BrowsePaginationState>({
    page: 0,
    perPage: initialPerPage,
    cursor: undefined,
  });

  function handleNextPage(nextCursor: string | null | undefined) {
    if (!nextCursor) {
      return;
    }

    setState((prev) => ({
      ...prev,
      page: prev.page + 1,
      cursor: nextCursor,
    }));
  }

  function handlePrevPage(prevCursor: string | null | undefined) {
    if (!prevCursor) {
      return;
    }

    setState((prev) => ({
      ...prev,
      page: Math.max(prev.page - 1, 0),
      cursor: prevCursor,
    }));
  }

  function handleRowsPerPageChange(perPage: number) {
    setState({
      page: 0,
      perPage,
      cursor: undefined,
    });
  }

  function resetPagination() {
    setState((prev) => ({
      ...prev,
      page: 0,
      cursor: undefined,
    }));
  }

  return {
    page: state.page,
    perPage: state.perPage,
    cursor: state.cursor,
    handleNextPage,
    handlePrevPage,
    handleRowsPerPageChange,
    resetPagination,
  };
}
